import { useState, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface CreateThreadModalProps {
  groupId: string
  onClose: () => void
  onSuccess: (threadId: string) => void
  /** Pre-fill title when opening from TV Guide (e.g. programme title). */
  initialTitle?: string
  /** Pre-fill body when opening from TV Guide (e.g. programme description). */
  initialBody?: string
}

export default function CreateThreadModal({ groupId, onClose, onSuccess, initialTitle = '', initialBody = '' }: CreateThreadModalProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [threadTitle, setThreadTitle] = useState(initialTitle)
  const [threadBody, setThreadBody] = useState(initialBody)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const titleInputRef = useRef<HTMLInputElement>(null)

  // Focus first input on mount
  useEffect(() => {
    titleInputRef.current?.focus()
  }, [])

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose, isSubmitting])

  const createThread = useMutation({
    mutationFn: async (data: { title: string; body: string }) => {
      if (!user) throw new Error('Not authenticated')
      if (!groupId) throw new Error('Group ID is required')

      const { data: thread, error: threadError } = await supabase
        .from('threads')
        .insert({
          group_id: groupId,
          author_id: user.id,
          title: data.title.trim(),
          body: data.body.trim(),
          is_hidden: false,
        })
        .select()
        .single()

      if (threadError) throw threadError

      return thread
    },
    onSuccess: (thread) => {
      // Invalidate threads query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['group-threads', groupId] })
      // Also invalidate activity-related queries
      queryClient.invalidateQueries({ queryKey: ['threads-from-my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['latest-threads'] })
      
      // Reset form
      setThreadTitle('')
      setThreadBody('')
      
      // Call onSuccess with the new thread ID
      onSuccess(thread.id)
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Basic validation
    if (!threadTitle.trim()) {
      setError('Thread title is required')
      return
    }

    if (threadTitle.trim().length < 3) {
      setError('Thread title must be at least 3 characters')
      return
    }

    if (!threadBody.trim()) {
      setError('Thread content is required')
      return
    }

    if (threadBody.trim().length < 10) {
      setError('Thread content must be at least 10 characters')
      return
    }

    setIsSubmitting(true)
    try {
      await createThread.mutateAsync({
        title: threadTitle.trim(),
        body: threadBody.trim(),
      })
    } catch (err: any) {
      setError(err.message || 'Failed to create thread')
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-thread-title"
    >
      <div
        className="rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '2px solid var(--bg-tertiary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 
            id="create-thread-title"
            className="text-2xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            Create New Thread
          </h2>
          <button
            onClick={onClose}
            className="p-2"
            aria-label="Close dialog"
            style={{ color: 'var(--text-primary)' }}
            disabled={isSubmitting}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Thread Title Field */}
          <div>
            <label 
              htmlFor="thread-title"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Thread Title
              <span style={{ color: 'var(--color-error)' }} aria-label="required"> *</span>
            </label>
            <input
              ref={titleInputRef}
              type="text"
              id="thread-title"
              value={threadTitle}
              onChange={(e) => setThreadTitle(e.target.value)}
              placeholder="Enter thread title"
              required
              minLength={3}
              maxLength={200}
              className="w-full px-4 py-2 border-2 rounded"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
              aria-required="true"
              disabled={isSubmitting}
            />
          </div>

          {/* Thread Body Field */}
          <div>
            <label 
              htmlFor="thread-body"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Thread Content
              <span style={{ color: 'var(--color-error)' }} aria-label="required"> *</span>
            </label>
            <textarea
              id="thread-body"
              value={threadBody}
              onChange={(e) => setThreadBody(e.target.value)}
              placeholder="Write your thread content here..."
              rows={8}
              required
              minLength={10}
              className="w-full px-4 py-2 border-2 rounded"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
              aria-required="true"
              disabled={isSubmitting}
            />
            <p 
              className="text-sm mt-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              Minimum 10 characters required
            </p>
          </div>

          {error && (
            <div 
              role="alert"
              className="p-3 rounded"
              style={{
                backgroundColor: 'var(--color-error)',
                color: 'var(--text-inverse)',
              }}
            >
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 font-semibold rounded disabled:opacity-50"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 font-semibold rounded disabled:opacity-50"
              style={{
                backgroundColor: 'var(--bg-interactive)',
                color: 'var(--text-inverse)',
              }}
            >
              {isSubmitting ? 'Creating...' : 'Create Thread'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

